<script setup lang="ts">
import { computed, ref } from 'vue';
import ImgPreview from '@/components/ImgPreview/ImgPreview.vue';
import { UNKNOWN_ALBUM_ART_PATH } from '@/constants/coverArt';

/**
 * Reusable square cover-art tile.
 *
 * Standardizes the styling we want everywhere a cover image appears
 * (rounded corners, border, muted bg, hover-to-zoom preview) and
 * provides sensible defaults for size and fallback image.
 *
 * Wraps {@link ImgPreview} so consumers automatically get the
 * popover-zoom behaviour in addition to the placeholder treatment.
 */
const {
    src = null,
    alt = 'Cover art',
    size = 96,
    previewSize = 480,
    fallbackSrc = UNKNOWN_ALBUM_ART_PATH,
    class: className = '',
} = defineProps<{
    src?: string | null;
    alt?: string;
    /** Rendered tile edge length in pixels (square). */
    size?: number;
    /** Edge length of the hover-zoom popover image. */
    previewSize?: number;
    fallbackSrc?: string;
    class?: string;
}>();

const wrapperClass = computed(() => {
    // Layout-related classes (margins, shrink-0, alignment, etc.) come from
    // the consumer via `class`; we own the visual chrome of the tile itself.
    return ['overflow-hidden rounded-md border bg-muted', className].filter(Boolean).join(' ');
});

const imgPreviewRef = ref<InstanceType<typeof ImgPreview> | null>(null);

/** Forwarded from ImgPreview so consumers whose tile is covered by another control (e.g. a play overlay) can open the zoom from their own button. */
defineExpose({
    openPreview: () => imgPreviewRef.value?.openPreview(),
});
</script>

<template>
    <ImgPreview
        ref="imgPreviewRef"
        :src="src"
        :alt="alt"
        :width="size"
        :height="size"
        :preview-width="previewSize"
        :fallback-src="fallbackSrc"
        :class="wrapperClass"
    />
</template>
